/* UAParser.js v0.7.28
   Copyright © 2012-2021 Faisal Salman <f@faisalman.com>
   MIT License
   Modified by David Wells
*/

/* eslint-disable
    no-useless-escape,
    no-var,
    no-extra-boolean-cast,
    @typescript-eslint/no-unsafe-assignment,
    @typescript-eslint/no-unsafe-call,
    @typescript-eslint/no-unnecessary-type-assertion,
    @typescript-eslint/prefer-for-of,
    @typescript-eslint/prefer-includes
    -- preserved UAParser v0.7.28 implementation */

const OBJ_TYPE = "object";
const STR_TYPE = "string";
const UNKNOWN = "?";
const NAME = "name";
const VERSION = "version";
const EMPTY = "";
const FUNC_TYPE = "function";
var BLACKBERRY = "BlackBerry",
    CHROME = "Chrome",
    FIREFOX = "Firefox";

const windowsVersionMap: Record<string, string | string[]> = {
    ME: "4.90",
    "NT 3.11": "NT3.51",
    "NT 4.0": "NT4.0",
    "2000": "NT 5.0",
    XP: ["NT 5.1", "NT 5.2"],
    Vista: "NT 6.0",
    "7": "NT 6.1",
    "8": "NT 6.2",
    "8.1": "NT 6.3",
    "10": ["NT 6.4", "NT 10.0"],
    RT: "ARM",
};

type ParserContext = Record<string, string | undefined>;
type UnaryMapper = (str: string) => string | undefined;
type StringMapper = (str: string, map: Record<string, string | string[]>) => string | undefined;
type ParserProperty =
    | string
    | readonly [string, string | UnaryMapper]
    | readonly [string, StringMapper | RegExp, Record<string, string | string[]> | string]
    | readonly [string, RegExp, string, (str: string) => string];
type RegexMap = readonly (readonly RegExp[] | readonly ParserProperty[])[];

const os: RegexMap = [
    [
        /* Windows */
        // Windows (iTunes)
        /microsoft (windows) (vista|xp)/i,
    ],
    [NAME, VERSION],
    // Windows RT
    [
        /(windows) nt 6\.2; (arm)/i,
        // Windows Phone
        /(windows (?:phone(?: os)?|mobile))[\/ ]?([\d\.\w ]*)/i,
        /(windows)[\/ ]?([ntce\d\. ]+\w)(?!.+xbox)/i,
    ],
    [NAME, [VERSION, strMapper, windowsVersionMap]],
    [/(win(?=3|9|n)|win 9x )([nt\d\.]+)/i],
    [
        [NAME, "Windows"],
        [VERSION, strMapper, windowsVersionMap],
    ],
    /* iOS/macOS */
    // iOS
    [/ip[honead]{2,4}\b(?:.*os ([\w]+) like mac|; opera)/i, /cfnetwork\/.+darwin/i],
    [
        [VERSION, /_/g, "."],
        [NAME, "iOS"],
    ],
    // Mac OS
    [/(mac os x) ?([\w\. ]*)/i, /(macintosh|mac_powerpc\b)(?!.+haiku)/i],
    [
        [NAME, "MacOS"],
        [VERSION, /_/g, "."],
    ],
    /* Mobile OSes */
    // Android-x86
    [/droid ([\w\.]+)\b.+(android[- ]x86)/i],
    [VERSION, NAME],
    // Android/WebOS/QNX/Bada/RIM/Maemo/MeeGo/Sailfish OS
    [
        /(android|webos|qnx|bada|rim tablet os|maemo|meego|sailfish)[-\/ ]?([\w\.]*)/i,
        // Blackberry
        /(blackberry)\w*\/([\w\.]*)/i,
        // Tizen/KaiOS
        /(tizen|kaios)[\/ ]([\w\.]+)/i,
        // Series 40
        /\((series40);/i,
    ],
    [NAME, VERSION],
    // BlackBerry 10
    [/\(bb(10);/i],
    [VERSION, [NAME, BLACKBERRY]],
    // Firefox OS
    [/mozilla\/[\d\.]+ \((?:mobile|tablet|tv|mobile; [\w ]+); rv:.+ gecko\/([\w\.]+)/i],
    [VERSION, [NAME, FIREFOX + " OS"]],
    // WebOS
    [/web0s;.+rt(tv)/i, /\b(?:hp)?wos(?:browser)?\/([\w\.]+)/i],
    [VERSION, [NAME, "webOS"]],
    // Google Chromecast
    [/crkey\/([\d\.]+)/i],
    // Google Chromecast
    [VERSION, [NAME, CHROME + "cast"]],
    // Chromium OS
    [/(cros) [\w]+ ([\w\.]+\w)/i],
    [[NAME, "Chromium OS"], VERSION],
    // Other
    [
        // Mageia/VectorLinux
        /(mageia|vectorlinux)[; ]/i,
        // Ubuntu/Debian/SUSE/Gentoo/Arch/Slackware/Fedora/Mandriva/CentOS/PCLinuxOS/RedHat/Zenwalk/Linpus/Raspbian/Plan9/Minix/RISCOS/Contiki/Deepin/Manjaro/elementary/Sabayon/Linspire
        /([kxln]?ubuntu|debian|suse|opensuse|gentoo|arch(?= linux)|slackware|fedora|mandriva|centos|pclinuxos|red ?hat|zenwalk|linpus|raspbian|plan 9|minix|risc os|contiki|deepin|manjaro|elementary os|sabayon|linspire)(?: gnu\/linux)?(?: enterprise)?(?:[- ]linux)?(?:-gnu)?[-\/ ]?(?!chrom|package)([-\w\.]*)/i,
        // Hurd/Linux
        /(hurd|linux) ?([\w\.]*)/i,
        // GNU
        /(gnu) ?([\w\.]*)/i,
        // UNIX
        /(unix) ?([\w\.]*)/i,
    ],
    [NAME, VERSION],
];

function rgxMapper(this: ParserContext, ua: string, arrays: RegexMap) {
    var i = 0,
        j,
        k,
        p,
        q,
        matches,
        match;
    // loop through all regexes maps
    while (i < arrays.length && !matches) {
        var regex = arrays[i] as readonly RegExp[], // even sequence (0,2,4,..)
            props = arrays[i + 1] as readonly ParserProperty[]; // odd sequence (1,3,5,..)
        j = k = 0;

        // try matching uastring with regexes
        while (j < regex.length && !matches) {
            matches = regex[j++].exec(ua);

            if (!!matches) {
                for (p = 0; p < props.length; p++) {
                    match = matches[++k];
                    q = props[p];
                    // check if given property is actually array
                    if (typeof q === OBJ_TYPE && q.length > 0) {
                        const property = q[0];

                        if (q.length == 2) {
                            const mapper = q[1] as string | UnaryMapper;
                            if (typeof mapper == FUNC_TYPE) {
                                // assign modified match
                                this[property] = (mapper as UnaryMapper).call(this, match);
                            } else {
                                // assign given value, ignore regex match
                                this[property] = mapper as string;
                            }
                        } else if (q.length == 3) {
                            const mapper = q[1] as StringMapper | RegExp;
                            // check whether function or regex
                            if (
                                typeof mapper === FUNC_TYPE &&
                                !((mapper as unknown as RegExp).exec && (mapper as unknown as RegExp).test)
                            ) {
                                // call function (usually string mapper)
                                this[property] = match
                                    ? (mapper as StringMapper).call(
                                          this,
                                          match,
                                          q[2] as Record<string, string | string[]>,
                                      )
                                    : undefined;
                            } else {
                                // sanitize match using given regex
                                this[property] = match ? match.replace(mapper as RegExp, q[2] as string) : undefined;
                            }
                        } else if (q.length == 4) {
                            const mapper = q[3] as (str: string) => string;
                            this[property] = match
                                ? mapper.call(this, match.replace(q[1] as RegExp, q[2] as string))
                                : undefined;
                        }
                    } else {
                        this[q as string] = match ? match : undefined;
                    }
                }
            }
        }
        i += 2;
    }
}

function strMapper(str: string, map: Record<string, string | string[]>): string | undefined {
    for (var i in map) {
        // check if current value is array
        if (typeof map[i] === OBJ_TYPE && map[i].length > 0) {
            for (var j = 0; j < map[i].length; j++) {
                if (has(map[i][j], str)) {
                    return i === UNKNOWN ? undefined : i;
                }
            }
        } else if (has(map[i] as string, str)) {
            return i === UNKNOWN ? undefined : i;
        }
    }
    return str;
}

function has(str1: string, str2: string): boolean {
    return typeof str1 === STR_TYPE ? lowerize(str2).indexOf(lowerize(str1)) !== -1 : false;
}

function lowerize(str: string): string {
    return str.toLowerCase();
}

export interface PinpointOs {
    name?: string;
    version?: string;
}

export function getPinpointOs(ua: string): PinpointOs {
    var _ua = ua || EMPTY;
    const parsedOs: ParserContext = {};
    parsedOs[NAME] = undefined;
    parsedOs[VERSION] = undefined;
    rgxMapper.call(parsedOs, _ua, os);
    return parsedOs;
}
